<script setup lang="ts">
import { computed, ref, watch } from "vue";

// MDS UI components import
import "@maersk-global/mds-components-core-input";
import "@maersk-global/mds-components-core-table";
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import "@maersk-global/mds-components-core-button";
import "@maersk-global/mds-components-core-pagination";
// Vue imports
import { AppSpecialCasesUtils } from "@/utils/special_cases";

import ContainerPrintView from "@/components/common/ContainerPrintView.vue";
import ShimmerLoaderTableView from "@/components/common/ShimmerLoaderTableView.vue";
import VesselDateTime from "@/components/common/VesselDateTime.vue";
import DrawerFilter from "@/components/schedule/DrawerFilter.vue";

import type { VesselSchedule } from "@/interface/schedule/vessel_schedule_interface";
import type { PaginationAttributes } from "@/interface/pagination_interface";

import { useVesselScheduleStore } from "@/store/schedule/vessel_schedule_store";
import { useScheduleLocationStore } from "@/store/schedule/schedule_location_store";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useScheduleSearchFilter } from "@/store/common/drawer_store";
import { useGlobalStore } from "@/store/global";
import ShippingCompany from "@/helper/linerCodes";
import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();
const terminalConfigurationStore = useTerminalConfigurationStore();
const locationStore = useScheduleLocationStore();
// Import the vessel schedule store
// Use the store
const vesselSchedule = useVesselScheduleStore();
// Checking for bg color for vessel status
const special = new AppSpecialCasesUtils();
// Drawer toggle state
const drawerFilterStore = useScheduleSearchFilter();
// Reactive getters
const schedules = computed((): VesselSchedule[] => vesselSchedule.filterSchedules);

const loader = computed((): boolean => vesselSchedule.isLoading);
const currentPage = ref<number>(1);
const isFilterApplied = ref<boolean>(false);
const isFileDownloading = ref<boolean>(false);

const pagination = ref<PaginationAttributes>({
  totalPages: 0,
  page: 1,
  pageSize: 25,
  totalRecords: 0
});

const selectedOneStreamCode = computed((): string => locationStore.terminalSelected.oneStreamCode);

let labels: any = {
  vesselSchedule: globalStore.sitecoreData.headings.vessel_schedule,
  scheduleLoader: globalStore.sitecoreData.common.vessel_loading,
  vesselDisclaimer: globalStore.sitecoreData.container_validation.schedule_disclaimer,
  filter: globalStore.sitecoreData.common.filter,
  globalSearch: globalStore.sitecoreData.common.vessel_search,
  csv: globalStore.sitecoreData.action_button.export_csv,
  print: globalStore.sitecoreData.action_button.print,
  resetBillOfLadingFilter: globalStore.sitecoreData.action_button.bolFilter,
  searchFilter: globalStore.sitecoreData.common.filter,
  colorGuide: globalStore.sitecoreData.table_columns.schedule.color_guide,
  actualTime: globalStore.sitecoreData.table_columns.schedule.actual_time,
  estimatedTime: globalStore.sitecoreData.table_columns.schedule.estimated_time,
  filtersApplied: globalStore.sitecoreData.headings.filtersApplied,
  remove: globalStore.sitecoreData.cta.remove,
  noVessels: globalStore.sitecoreData.search.no_search_result
};

const getCompanyName = (key: any): any => ShippingCompany[key];

const getPageCount = computed((): number => {
  return pagination.value.totalPages;
});

// Page change
const paginationChange = (event: any): void => {
  appButtonClick("Vessel Schedule", "Pagination Click", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  currentPage.value = event.detail;
};

watch(
  (): any => vesselSchedule.pagination,
  (data: PaginationAttributes): void => {
    pagination.value = { ...data };
    labels = {
      ...labels,
      downloadBtnLabel: globalStore.sitecoreData.terminal_specific[selectedOneStreamCode.value]
        ? globalStore.sitecoreData.terminal_specific[selectedOneStreamCode.value].download_button
        : "",
      downloadBtnLink: globalStore.sitecoreData.terminal_specific[selectedOneStreamCode.value]
        ? globalStore.sitecoreData.terminal_specific[selectedOneStreamCode.value].download_link
        : "",
      downloadSectionTile: globalStore.sitecoreData.terminal_specific[selectedOneStreamCode.value]
        ? globalStore.sitecoreData.terminal_specific[selectedOneStreamCode.value].download_section_title
        : ""
    };
  },
  { immediate: true, deep: true }
);

const getTerminalSpecificLabel = (fieldName: string): string => {
  const selectedOneStreamCode = locationStore.terminalSelected.oneStreamCode;
  if (globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.schedule) {
    return (
      globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.schedule[fieldName] ||
      globalStore.sitecoreData.table_columns.schedule[fieldName]
    );
  }
  return globalStore.sitecoreData.table_columns.schedule[fieldName];
};

const containerColumnNames = computed((): any => {
  return terminalConfigurationStore.scheduleConfiguration.containerColumnNames
    .filter((item: any): any => item.enabled) // Filter items where enabled is true
    .map((item: any) => {
      const fieldName = `${item.id}`;
      return {
        ...item,
        label: getTerminalSpecificLabel(fieldName),
        width: fieldName.includes("Date") ? "150px" : "130px"
      };
    });
});

const openDrawer = (): void => {
  drawerFilterStore.toggleDrawer();
};

const clearFilterHandler = () => {
  drawerFilterStore.clearFilters();
  vesselSchedule.clearVesselDrawerFilters(drawerFilterStore.getVesselFilterValues.drawerFilters);
  isFilterApplied.value = false;
};

const applyFilterHandler = () => {
  appButtonClick("Vessel Schedule", "Filter Apply", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  vesselSchedule.searchVesselDrawerFilters(drawerFilterStore.getVesselFilterValues.drawerFilters);
  isFilterApplied.value = true;
};

const isFiltersBlank = () => {
  const filters = drawerFilterStore.getVesselFilterValues.drawerFilters;
  const hasFilterValues =
    filters.vesselName.trim() !== "" ||
    filters.vesselStatus.trim() !== "" ||
    filters.vesselBerth.trim() !== "" ||
    Object.entries(filters).some(
      ([, value]) =>
        typeof value === "object" &&
        value !== null &&
        "from" in value &&
        "to" in value &&
        typeof value.from === "string" &&
        typeof value.to === "string" &&
        (value.from.trim() !== "" || value.to.trim() !== "")
    );
  return hasFilterValues;
};
const rowItemClickHandler = (id: number) => {
  const mcTable = document.querySelector("mc-table");
  appButtonClick("Vessel Schedule", "Show Shipping Lines Click", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  if (mcTable.expandopened.length) {
    mcTable.expandopened.filter((item) => {
      if (item.id === id) {
        mcTable.expandopened = [];
        return;
      } else {
        mcTable.expandopened = [];
        mcTable.expandopened = [schedules.value.find((row) => row.id === id)];
      }
    });
  } else {
    mcTable.expandopened = [schedules.value.find((row) => row.id === id)];
    return;
  }
};

const downloadorOpenFile = (url: string) => {
  isFileDownloading.value = true;
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes(".pdf")) {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else if (lowerUrl.includes(".jpeg") || lowerUrl.includes(".jpg")) {
    window.open(url, "_blank"); // open image in new tab
  } else {
    window.open(url, "_blank"); // open other link in new tab
  }
  isFileDownloading.value = false;
};

const handleSortChange = (event: any) => {
  appButtonClick("Vessel Schedule", "Sort Button Click", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails,
    sortColumnName: event.detail.column,
    sortDirection: event.detail.direction
  });
};

const appnedDateInUrl = (url: string) => {
  if (url.includes(".pdf") || url.includes(".jpg") || url.includes(".png") || url.includes(".jpeg")) {
    return url + `?rev=${new Date().getTime()}`;
  }
  return url;
};
const showAlertNotification = () => {
  if (globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message) {
    return (
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message &&
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message !==
        ""
    );
  }
  return false;
};
</script>

<template>
  <div v-if="showAlertNotification()" class="mrgn-btm-10">
    <mc-notification
      heading=""
      :body="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.message
      "
      :appearance="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.category
      "
    >
    </mc-notification>
  </div>
  <DrawerFilter @apply-handler="applyFilterHandler" @clear-handler="clearFilterHandler"></DrawerFilter>
  <div class="main-content">
    <div v-if="loader" class="search-card">
      <ShimmerLoaderTableView :msg="labels.scheduleLoader"></ShimmerLoaderTableView>
    </div>
    <div v-if="vesselSchedule.vesselSearch && !loader">
      <div class="page-header button-top-margin-15px">
        <h3>{{ labels.vesselSchedule }} ({{ schedules.length }})</h3>
      </div>
      <div
        v-if="
          selectedOneStreamCode === 'NG004L' || selectedOneStreamCode === 'NG009L' || selectedOneStreamCode === 'EG005U'
        "
        class="download_container"
      >
        <div class="download_container_btn">
          <div v-if="labels.downloadSectionTile !== ''" class="btn_title">
            {{ labels.downloadSectionTile }}
          </div>
          <mc-button
            :label="labels.downloadBtnLabel"
            fit="large"
            appearance="primary"
            variant="outlined"
            width="full-width"
            :loading="isFileDownloading"
            @click="downloadorOpenFile(appnedDateInUrl(labels.downloadBtnLink))"
          >
          </mc-button>
        </div>
      </div>
      <div v-if="selectedOneStreamCode === 'PE018U'" class="download_container display_flex">
        <div class="paddingTop20">
          <span>{{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].extranet_text }}</span>
          <span class="font_bold"
            ><a
              :href="
                appnedDateInUrl(globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].extranet_text_link)
              "
              target="_blank"
              >{{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].extranet_text_key }}</a
            ></span
          >
        </div>
        <div class="download_container_btn">
          <div class="download_container_btn_padding">
            <mc-button
              :label="globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].daily_berthing_plan_btn"
              fit="large"
              appearance="primary"
              variant="outlined"
              width="full-width"
              :loading="isFileDownloading"
              @click="
                downloadorOpenFile(
                  appnedDateInUrl(
                    globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].daily_berthing_plan_link
                  )
                )
              "
            >
            </mc-button>
          </div>
          <div>
            <mc-button
              :label="globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].vessel_list_btn"
              fit="large"
              appearance="primary"
              variant="outlined"
              width="full-width"
              :loading="isFileDownloading"
              @click="
                downloadorOpenFile(
                  appnedDateInUrl(globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].vessel_list_link)
                )
              "
            >
            </mc-button>
          </div>
        </div>
      </div>
      <div v-if="selectedOneStreamCode === 'ES075E'">
        <div class="paddingTop20">
          <ul class="custom-dot-list">
            <li>
              <span class="font_bold"
                ><a
                  :href="
                    appnedDateInUrl(
                      globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].explore_other_tools_link
                    )
                  "
                  target="_blank"
                  >{{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].explore_other_tools_text }}</a
                ></span
              >
            </li>
          </ul>
        </div>
      </div>
      <div v-if="selectedOneStreamCode === 'IT059E'">
        <div class="paddingTop20">
          <ul class="custom-dot-list">
            <li>
              <span class="font_bold">{{
                globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].custom_cut_off_key
              }}</span>
              <span>
                {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].custom_cut_off_desc }}
              </span>
            </li>
            <li>
              <span class="font_bold">{{
                globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].exceptional_changes
              }}</span>
              <span>
                {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].exceptional_changes_desc1 }}
              </span>
              <span>
                <a
                  :href="
                    'mailto:' +
                    globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].exceptional_changes_mail_id
                  "
                >
                  {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].exceptional_changes_mail_id }}
                </a>
              </span>
              <span>
                {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].exceptional_changes_desc2 }}
              </span>
            </li>
            <li>
              <span class="font_bold">{{
                globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].container_terminal
              }}</span>
              <span>
                {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].container_terminal_desc }}
              </span>
            </li>
            <li>
              <span class="font_bold">{{
                globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].refer_terminal
              }}</span>
              <span>
                {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].refer_terminal_desc }}
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div v-if="selectedOneStreamCode === 'IN015L'" class="download_container_btn">
        <div class="btn_title">
          {{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].download_button_title }}
        </div>
        <div class="icon_link">
          <mc-icon icon="tray-arrow-down" color="#ff6441"></mc-icon
          ><a
            :href="appnedDateInUrl(globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].download_link)"
            target="_blank"
            >{{ globalStore.sitecoreData.terminal_specific[selectedOneStreamCode].download_button }}</a
          >
        </div>
      </div>
      <div class="spacer"></div>

      <div ref="importResultScrollTarget" class="search-card">
        <div class="devider"></div>
        <div class="container-action-buttons">
          <div style="width: 50%">
            <div class="time_tags">
              <span class="color_guide_text">{{ labels.colorGuide }}:</span>
              <span class="horizontal-margin-10px"></span>
              <span class="actual_tag">{{ labels.actualTime }} </span>
              <span class="horizontal-margin-10px"></span>
              <span class="estimated_tag">{{ labels.estimatedTime }} </span>
            </div>
          </div>
          <div class="action-buttons">
            <mc-button
              icon="sliders-horizontal"
              :label="labels.searchFilter"
              variant="plain"
              appearance="primary"
              @click="openDrawer()"
            >
            </mc-button>
            <ContainerPrintView
              :containers="schedules"
              :fileName="'Vessel-Schedule'"
              :headerLabel="containerColumnNames"
              :is-disabled="!schedules.length"
              :terminalOneStreamCode="selectedOneStreamCode"
              :analyticsTerminalDetails="locationStore.anayticsTerminalDetails"
            />
          </div>
          <!-- Container watch list and export and pdf print all -->
        </div>
        <div class="my-table-container">
          <div v-if="isFiltersBlank() && isFilterApplied">
            <div class="filters_applies_section">
              <span>
                <mc-icon icon="info-circle" class="filters_applies_section__icon"> </mc-icon>
                {{ labels.filtersApplied }}
              </span>
              <mc-button :label="labels.remove" variant="plain" appearance="primary" @click="clearFilterHandler">
              </mc-button>
            </div>
          </div>
          <mc-table
            :data.prop="schedules"
            :columns.prop="containerColumnNames"
            :recordsperpage="pagination.pageSize"
            :currentpage="currentPage"
            class="retender-loads-table"
            sortdefaultcolumnid="estimatedArrivalDateTimeLocal"
            sortdefaultdirection="ascending"
            expand
            fit="small"
            selectSticky
            @sortchange="handleSortChange($event)"
          >
            <div v-if="!schedules.length" slot="state">{{ labels.noVessels }}</div>
            <template v-for="row in schedules" :key="row.id">
              <div :slot="`${row.id}_vesselName`">
                <h6 class="text-underscore" @click="rowItemClickHandler(row.id)">{{ row.vesselName }}</h6>
                <div class="vessel-inbounds">
                  <span class="color-text">{{ row.inboundVoyageNumber }}</span>
                  <span> | </span>
                  <span class="color-text">{{ row.outboundVoyageNumber }}</span>
                </div>
              </div>

              <div :slot="`${row.id}_vesselType`">
                {{ row.vesselClassification }}
              </div>

              <!-- Line -->
              <div :slot="`${row.id}_shippingLineOperator`">
                {{ row.vesselOperator.toUpperCase() }}
              </div>

              <div :slot="`${row.id}_expanded`" class="review__corridor-comment">
                <div class="item-container">
                  <div v-for="(item, index) in row.shippingLines" :key="index" class="item-card">
                    <span>
                      {{ !getCompanyName(item) || getCompanyName(item) === "" ? item : getCompanyName(item) }}
                    </span>
                  </div>
                </div>
              </div>

              <div :slot="`${row.id}_vesselStatus`" class="maxWidth160">
                <div :class="`padding-border ${special.getBGColor(row.vesselStatus.toLocaleLowerCase())}`">
                  {{
                    globalStore.sitecoreData.table_columns.schedule[row.vesselStatus.toLocaleLowerCase()] ||
                    row.vesselStatus
                  }}
                </div>
              </div>
              <!-- Scheduled arrival time -->
              <VesselDateTime
                :id="`${row.id}_scheduledArrivalDateTimeLocal`"
                :icon="'check-circle-half-dashed'"
                :actual="row.scheduledArrivalDateTimeLocal"
                :estimated="row.scheduledArrivalDateTimeLocal"
              />
              <!-- Estimated or Actual arrival time -->
              <VesselDateTime
                :id="`${row.id}_estimatedArrivalDateTimeLocal`"
                :showETA="true"
                :actual="row.actualArrivalDateTimeLocal"
                :estimated="row.estimatedArrivalDateTimeLocal"
              />
              <!-- First available time -->
              <VesselDateTime
                :id="`${row.id}_firstAvailableDateTimeLocal`"
                :actual="row.firstAvailableDateTimeLocal"
                :estimated="row.firstAvailableDateTimeLocal"
              />
              <!-- Scheduled departure time -->
              <VesselDateTime
                :id="`${row.id}_scheduledDepartureDateTimeLocal`"
                :icon="'check-circle-half-dashed'"
                :actual="row.scheduledDepartureDateTimeLocal"
                :estimated="row.scheduledDepartureDateTimeLocal"
              />
              <!-- Actual departure time -->
              <VesselDateTime
                :id="`${row.id}_estimatedDepartureDateTimeLocal`"
                :showETA="true"
                :actual="row.actualDepartureDateTimeLocal"
                :estimated="row.estimatedDepartureDateTimeLocal"
                :sendDeparture="true"
              />

              <!-- Start receiving date time -->
              <VesselDateTime
                :id="`${row.id}_gateOpenDateTimeLocal`"
                :actual="row.startReceiveDateTimeLocal"
                :estimated="row.startReceiveDateTimeLocal"
              />
              <VesselDateTime
                :id="`${row.id}_startReceiveDateTimeLocal`"
                :actual="row.startReceiveDateTimeLocal"
                :estimated="row.startReceiveDateTimeLocal"
              />
              <!-- Cargo cutoff date time -->
              <VesselDateTime
                :id="`${row.id}_cargoCutoffDateTimeLocal`"
                :actual="row.cargoCutoffDateTimeLocal"
                :estimated="row.cargoCutoffDateTimeLocal"
              />

              <!-- Doc cutoff date time -->
              <VesselDateTime
                :id="`${row.id}_docCutOff`"
                :actual="row.hazardousCutoffDateTimeLocal"
                :estimated="row.hazardousCutoffDateTimeLocal"
              />

              <!-- Reefer cutoff date time -->
              <VesselDateTime
                :id="`${row.id}_reeferCutoffDateTimeLocal`"
                :actual="row.reeferCutoffDateTimeLocal"
                :estimated="row.reeferCutoffDateTimeLocal"
              />
              <!-- Hazardous cutoff date time -->
              <VesselDateTime
                :id="`${row.id}_hazardousCutoffDateTimeLocal`"
                :actual="row.hazardousCutoffDateTimeLocal"
                :estimated="row.hazardousCutoffDateTimeLocal"
              />
            </template>
          </mc-table>
        </div>
        <div v-if="schedules.length > 25" class="my-table-pagination">
          <mc-pagination
            fit="small"
            :totalpages="getPageCount"
            previouslabel=""
            nextlabel=""
            @pagechange="paginationChange"
          ></mc-pagination>
        </div>
      </div>
      <div class="spacer"></div>
      <div class="discliamer_section">{{ labels.vesselDisclaimer }}</div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.container-action-buttons {
  justify-content: space-between;
}
.time_tags {
  margin-top: 15px;
}
.filters_applies_section {
  background: #f7f4e7;
  padding: 0 20px;
  margin-bottom: 1px;
  border-radius: 5px;
  &__icon {
    display: inline-block;
    vertical-align: text-bottom;
  }
}
.actual_tag {
  background-color: #ff6441;
  color: white;
  padding: 3px 15px;
  border-radius: 15px;
  font-size: 14px;
}
.estimated_tag {
  background-color: #42b0d5;
  color: white;
  padding: 3px 15px;
  border-radius: 15px;
  font-size: 14px;
}
.color_guide_text {
  font-weight: bold;
  color: #454545;
  font-size: 14px;
}
</style>
